import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PillBottle, ArrowRight, X } from 'lucide-react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { usePortalDestination } from '../PortalDestinationContext';

const dueText = (dueDate) => {
  if (!dueDate) return 'due soon';
  const days = Math.round((new Date(`${dueDate}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (days < 0) return `ran out ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'runs out today';
  if (days === 1) return 'runs out tomorrow';
  return `runs out in ${days} days`;
};

// Refill nudges pushed by the pharmacy's reminder cron. This is the channel
// that works with no SMS or email account configured, and the only one that
// can hand the patient a pre-filled request instead of a phone number.
export const PortalRefillNudges = ({ limit } = {}) => {
  const { api, href } = usePortalDestination();
  const navigate = useNavigate();
  const [nudges, setNudges] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/refill-nudges');
      setNudges(data.nudges || []);
    } catch { /* a missing nudge list should never break the dashboard */ }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (nudgeId) => {
    setNudges((rows) => rows.filter((r) => r.nudge_id !== nudgeId));
    try {
      await api.post(`/refill-nudges/${nudgeId}/dismiss`);
    } catch {
      toast.error('Could not dismiss that reminder');
      load();
    }
  };

  const requestRefill = (nudge) => {
    // Dismiss as we hand off: the card has done its job, and if the patient
    // abandons the form the next reminder round pushes a fresh one. The
    // medication rides along as the request note — Care refills are linked to
    // an existing prescription, so there is no free-text item list to fill.
    dismiss(nudge.nudge_id);
    navigate(href('refills'), {
      state: { prefillNote: `Refill request: ${nudge.product_name}` },
    });
  };

  if (nudges.length === 0) return null;
  const visible = limit ? nudges.slice(0, limit) : nudges;

  return (
    <section className="space-y-3" data-testid="portal-refill-nudges">
      {visible.map((nudge) => (
        <Card key={nudge.nudge_id} className="border-amber-300 bg-amber-50/70" data-testid={`portal-refill-nudge-${nudge.nudge_id}`}>
          <CardContent className="p-4 flex items-start gap-3">
            <div className="p-2.5 rounded-2xl bg-amber-100 text-amber-700 shrink-0">
              <PillBottle className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900">Time to refill {nudge.product_name}</p>
              <p className="text-sm text-slate-600">
                Your supply {dueText(nudge.due_date)} · {nudge.branch_name}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  size="sm" onClick={() => requestRefill(nudge)}
                  data-testid={`portal-refill-nudge-request-${nudge.nudge_id}`}
                  className="gap-1.5"
                >
                  Request refill <ArrowRight className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm" variant="ghost" onClick={() => dismiss(nudge.nudge_id)}
                  data-testid={`portal-refill-nudge-dismiss-${nudge.nudge_id}`}
                >
                  Not now
                </Button>
              </div>
            </div>
            <button
              type="button" onClick={() => dismiss(nudge.nudge_id)}
              className="text-slate-400 hover:text-slate-600 shrink-0"
              aria-label="Dismiss reminder"
              data-testid={`portal-refill-nudge-close-${nudge.nudge_id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </CardContent>
        </Card>
      ))}
    </section>
  );
};
